
import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { Tutorial, TutorialStep } from "@/types/tutorial";

interface TutorialPreviewProps {
  tutorial: Partial<Tutorial>;
}

const TutorialPreview: React.FC<TutorialPreviewProps> = ({ tutorial }) => {
  const handleStepToggle = (sectionId: string, stepId: string, completed: boolean) => {
    // In preview mode, this is just for visual demonstration
    console.log(`Step ${stepId} in section ${sectionId} toggled to ${completed}`);
  };

  // Sort BOM items by categories
  const sortedBom = React.useMemo(() => {
    if (!tutorial.bom || tutorial.bom.length === 0) return [];
    
    const bomWithCategories = tutorial.bom.map(item => ({
      ...item,
      sortKey: item.categories && item.categories.length > 0 ? item.categories[0] : 'Uncategorized'
    }));
    
    return bomWithCategories.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [tutorial.bom]);

  return (
    <div className="space-y-6">
      {/* Tutorial Header */}
      <Card className="p-6">
        <div className="space-y-4">
          {tutorial.coverImage && (
            <div className="aspect-video w-full bg-muted rounded-lg overflow-hidden">
              <img 
                src={tutorial.coverImage} 
                alt={tutorial.title || "Tutorial cover"} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden w-full h-full bg-muted flex items-center justify-center text-muted-foreground">
                No image
              </div>
            </div>
          )}
          
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {tutorial.title || "Untitled Tutorial"}
            </h1>
            
            <div 
              className="prose prose-sm max-w-none mb-4 text-muted-foreground [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:mb-3 [&_ol]:mb-3 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:dark:border-gray-600 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4 [&_code]:bg-gray-100 [&_code]:dark:bg-gray-800 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_strong]:font-bold [&_em]:italic [&_s]:line-through [&_u]:underline [&_table]:border-collapse [&_td]:border [&_th]:border [&_hr]:border-gray-300 [&_hr]:dark:border-gray-600 [&_.note-block]:rounded-r-lg [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-800 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:font-mono [&_pre]:text-sm [&_pre]:overflow-x-auto [&_pre]:border [&_pre]:border-gray-200 [&_pre]:dark:border-gray-700"
              dangerouslySetInnerHTML={{ 
                __html: tutorial.shortDescription || "No description provided." 
              }}
            />
            
            <div className="flex flex-wrap gap-2 mb-4">
              {tutorial.tags?.map((tag) => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Bill of Materials */}
      {sortedBom.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Bill of Materials</h2>
          <div className="space-y-2">
            {sortedBom.map((item) => (
              <div key={item.id} className="flex justify-between items-start p-2 bg-muted/50 rounded">
                <div className="flex-1">
                  <span className="font-medium">{item.name || "Unnamed item"}</span>
                  {item.categories && item.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.categories.map((category, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {category}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {item.description && (
                    <div 
                      className="text-sm text-muted-foreground mt-1 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  )}
                </div>
                <div className="text-sm flex flex-col items-end gap-1">
                  <span>Qty: {item.quantity}</span>
                  {item.links && item.links.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.links.map((link) => (
                        <Button 
                          key={link.id}
                          variant="outline" 
                          size="sm" 
                          asChild
                          className="h-6 px-2 text-xs"
                        >
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1"
                          >
                            <ExternalLink size={10} />
                            {link.name || "Link"}
                          </a>
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Tutorial Sections */}
      {tutorial.sections && tutorial.sections.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Tutorial Steps</h2>
          <Accordion type="multiple" className="space-y-4">
            {tutorial.sections.map((section, sectionIndex) => (
              <AccordionItem key={section.id} value={section.id} className="border rounded-lg">
                <AccordionTrigger className="px-4 py-2 hover:no-underline">
                  <span className="font-medium">
                    {section.title || `Section ${sectionIndex + 1}`}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    {section.steps?.map((step, stepIndex) => (
                      <div key={step.id} className="border rounded-lg p-4 bg-background">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={step.completed}
                            onCheckedChange={(checked) => 
                              handleStepToggle(section.id, step.id, checked as boolean)
                            }
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium mb-2">Step {stepIndex + 1}</h4>
                            {/* Render rich HTML content with proper styling */}
                            <div 
                              className="prose prose-sm max-w-none mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:mb-3 [&_ol]:mb-3 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:dark:border-gray-600 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4 [&_code]:bg-gray-100 [&_code]:dark:bg-gray-800 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_strong]:font-bold [&_em]:italic [&_s]:line-through [&_u]:underline [&_table]:border-collapse [&_td]:border [&_th]:border [&_hr]:border-gray-300 [&_hr]:dark:border-gray-600 [&_.note-block]:rounded-r-lg [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-800 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:font-mono [&_pre]:text-sm [&_pre]:overflow-x-auto [&_pre]:border [&_pre]:border-gray-200 [&_pre]:dark:border-gray-700"
                              dangerouslySetInnerHTML={{ 
                                __html: step.text || "No instructions provided." 
                              }}
                            />
                            {step.images && step.images.length > 0 && (
                              <div className="grid grid-cols-2 gap-2">
                                {step.images.map((image, imageIndex) => (
                                  <div key={imageIndex} className="aspect-video bg-muted rounded overflow-hidden">
                                    <img 
                                      src={image} 
                                      alt={`Step ${stepIndex + 1} image ${imageIndex + 1}`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                      }}
                                    />
                                    <div className="hidden w-full h-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                                      Invalid image
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )) || (
                      <p className="text-muted-foreground text-sm">No steps added yet.</p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      )}

      {(!tutorial.sections || tutorial.sections.length === 0) && (
        <Card className="p-6">
          <div className="text-center py-8 text-muted-foreground">
            <p>Start creating your tutorial by adding sections and steps.</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default TutorialPreview;
